/*
 * Freebird Stores — Keep focus inside the cart drawer after a line changes.
 *
 * WHY THIS FILE EXISTS, and it is a narrower reason than the plan
 * assumed. DEV-94 was written expecting to build focus RETURN — Figma's
 * frames annotate "focus returns to Add to Cart on dismiss", and a
 * <dialog> returns focus to whatever opened it, which reads like the
 * cart icon. Tracing it showed base already does the right thing:
 *
 *   - assets/dialog.js opens with `dialog.showModal()`, which records
 *     the currently focused element and restores it on close.
 *   - On the add path nothing steals focus first. assets/product-form.js
 *     does call `disable()` on the add-to-cart button, but only on the
 *     quantity-validation failure branch (which returns early and opens
 *     no drawer) and on variant selection — never while the add request
 *     is in flight.
 *
 * So the shopper's focus is still on Add to Cart when showModal runs,
 * and dismissing returns it there. That half needed no code, and a
 * "fix" for it would have been a fix for a non-problem — the failure
 * mode recorded in
 * docs/solutions/theme-architecture/extend-a-base-component-through-its-public-method.md
 *
 * WHAT IS ACTUALLY BROKEN is the other direction: changing the cart
 * from inside the drawer. assets/component-cart-items.js calls
 * `morphSection`, and neither that nor assets/morph.js touches focus.
 * Remove a line and the button holding focus ceases to exist, so the
 * browser's focus-fixup rule drops focus to <body> — mid-interaction,
 * inside an open modal, with the next Tab restarting from the top of
 * the document.
 *
 * The one-item cart is the case that matters and the one a casual test
 * misses: with two or more lines the morph usually leaves a comparable
 * control in place, so focus survives by luck. Remove the only item and
 * there is nothing left to land on. That asymmetry is why
 * scripts/keyboard-walkthrough/ needs a deliberately single-item
 * fixture — a check that cannot reach the bug state is not a check:
 * docs/solutions/conventions/a-check-that-cannot-reach-the-bug-state-is-not-a-check.md
 */

const CART_EVENT = 'cart:update';

/* THE DESTINATION MUST BE A CONTROL THAT CANNOT BE SPENT.
 *
 * The close button qualifies and almost nothing else in this drawer
 * does. Checkout is disabled on an empty cart; View Cart loses its href
 * there; a neighbouring line's Remove button may itself be removed by
 * the same update. Handing focus to any of those reproduces the defect
 * one step later — `.focus()` on a disabled element is a silent no-op
 * and focus stays on the dying element.
 *
 * That is the rule in
 * docs/solutions/theme-architecture/rescue-focus-after-disabling-not-before.md,
 * which this repo has now shipped a violation of twice.
 */
const RESCUE_SELECTOR = '.cart-drawer__close-button';

/** @param {Element | null} node */
function isInsideOpenDialog(node) {
  const dialog = node?.closest?.('dialog');
  return Boolean(dialog && dialog.open);
}

/* Which cart line a node belongs to. `data-key` is Shopify's line key,
   emitted on the row in snippets/cart-products.liquid, and it is the only
   thing that survives a morph as an identity rather than a position.
   @param {Element | null} node */
function lineKeyOf(node) {
  return node?.closest?.('[data-key]')?.getAttribute('data-key') ?? null;
}

/** @param {string | null} keyBefore The line focus sat on when the event fired. */
function rescueFocus(keyBefore) {
  const dialog = document.querySelector('.cart-drawer__dialog');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  const active = document.activeElement;

  /* Only rescue when focus has genuinely been lost. If the morph left
     the shopper on a real control — or they had already tabbed
     elsewhere inside the drawer — moving them is worse than doing
     nothing, because it silently undoes a deliberate action. */
  if (active && active !== document.body && isInsideOpenDialog(active)) {
    /* EXCEPT WHEN THE CONTROL IS NO LONGER THE SAME LINE'S, which looks
       identical to "focus survived" and is the more dangerous of the two.

       Removing one line of several does not destroy the focused button.
       `morphSection` calls `morph()` with no options, so its node key
       falls back to `node.id`; the row carries only `data-key`, so rows
       are matched POSITIONALLY. The row that held focus is reused to
       render whatever line now occupies that slot, and the shopper is
       left on a Remove button that has silently changed which product it
       removes — same element, new `aria-label`, no announcement. A second
       Enter deletes the wrong boot, and `.cart-items-disabled` is only
       `pointer-events: none`, which does not stop a keyboard activation.

       So identity, not survival, is the test. Same line: leave focus
       alone. Different line: the morph moved the shopper without telling
       them, and that is a loss even though a control is focused. */
    const keyNow = lineKeyOf(active);
    const repointed = Boolean(keyBefore && keyNow && keyNow !== keyBefore);
    if (!repointed) return;
  }

  const rescue = dialog.querySelector(RESCUE_SELECTOR);
  if (rescue instanceof HTMLElement) rescue.focus();
}

/* The event fires BEFORE the morph, not after.
 *
 * assets/component-cart-items.js dispatches CartUpdateEvent and only
 * then calls `morphSection`, so a listener that checks focus
 * synchronously inspects the DOM that is about to be replaced and always
 * finds focus intact. Waiting two frames lets the morph land and the
 * browser run its focus-fixup rule first, which is the state worth
 * measuring.
 *
 * NOT BECAUSE `morphSection` IS ASYNC — this comment said that until
 * 2026-08-27 and it was wrong, which matters because the wrong reason
 * sends the next person's thinking about listener ordering the wrong
 * way. `morphSection` in assets/section-renderer.js is DECLARED async,
 * but its body contains no `await`: it parses, resolves two elements and
 * calls `morph()`, which is synchronous apart from one `queueMicrotask`.
 * The replacement therefore lands inside this same dispatch. The two
 * frames are not waiting for a promise to settle — they are getting past
 * the dispatch entirely, which is also why anything reading the result
 * must RE-QUERY rather than hold a reference from before it.
 * assets/freebird-cart-banner.js waits the same two frames for the same
 * reason, and had to learn the re-query half the hard way.
 */
/* TWO FRAMES ARE ENOUGH ONLY WHEN THE HTML CAME WITH THE EVENT.
 *
 * Everything above is true of base's own path, where the event carries
 * `detail.data.sections` and the replacement lands inside this same
 * dispatch. An event with NO `sections` key takes the other branch of
 * `#handleCartUpdate` in assets/component-cart-items.js —
 * `sectionRenderer.renderSection(id, { cache: false })` — which awaits a
 * network fetch for the section HTML. That lands hundreds of
 * milliseconds after these two frames have already run and found focus
 * perfectly intact, and the rescue then never fires for the swap that
 * actually destroys the row.
 *
 * `assets/freebird-cart-sync.js` dispatches exactly that shape, on
 * purpose — omitting `sections` is what makes base re-render from the
 * server — so this is a live path, not a hypothetical one. This theme
 * has shipped the focus-drops-to-`<body>` defect twice; a fix that only
 * covers the synchronous path would be the third.
 *
 * OBSERVED, NOT TIMED. A second fixed delay would be the same guess with
 * a bigger number, and would still lose on a slow connection. The
 * observer watches the drawer's own subtree for the swap and gives up on
 * its own, so a re-render that never arrives costs one disconnected
 * observer rather than a pending timer.
 */
const LATE_RENDER_TIMEOUT_MS = 3000;

/** @param {string | null} keyBefore */
function rescueAfterLateRender(keyBefore) {
  const dialog = document.querySelector('.cart-drawer__dialog');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  let timer = 0;
  const observer = new MutationObserver(() => {
    /* Same two-frame wait as the synchronous path, for the same reason:
       get past the mutation and let the browser's focus fixup run before
       measuring where focus actually ended up. */
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        stop();
        rescueFocus(keyBefore);
      })
    );
  });

  const stop = () => {
    clearTimeout(timer);
    observer.disconnect();
  };

  observer.observe(dialog, { childList: true, subtree: true });
  timer = setTimeout(stop, LATE_RENDER_TIMEOUT_MS);
}

document.addEventListener(CART_EVENT, (event) => {
  /* Read the line identity SYNCHRONOUSLY, before the morph. Two frames
     later the row has already been rewritten, so the only chance to learn
     which line the shopper was on is now. */
  const keyBefore = lineKeyOf(document.activeElement);
  requestAnimationFrame(() => requestAnimationFrame(() => rescueFocus(keyBefore)));

  /* @type {any} */
  const detail = /** @type {any} */ (event).detail;
  if (!detail?.data?.sections) rescueAfterLateRender(keyBefore);
});
